// For more information about this file see https://dove.feathersjs.com/guides/cli/client.html
import { feathers } from '@feathersjs/feathers';
import type { TransportConnection, Application } from '@feathersjs/feathers';
import authenticationClient from '@feathersjs/authentication-client';
import type { AuthenticationClientOptions } from '@feathersjs/authentication-client';

import { pendingFilmClient } from './services/pending-film/pending-film.shared';
export type {
  PendingFilm,
  PendingFilmData,
  PendingFilmQuery,
  PendingFilmPatch,
} from './services/pending-film/pending-film.shared';
export { FormatTier } from './services/film/film.shared';

import { pendingEditClient } from './services/pending-edit/pending-edit.shared';
export type {
  PendingEdit,
  PendingEditData,
  PendingEditQuery,
  PendingEditPatch,
} from './services/pending-edit/pending-edit.shared';

import { uhdListsClient } from './services/uhd-lists/uhd-lists.shared';
export type {
  UhdLists,
  UhdListsData,
  UhdListsQuery,
  UhdListsPatch,
} from './services/uhd-lists/uhd-lists.shared';

import { listFilmsClient } from './services/list-films/list-films.shared';
export type {
  ListFilm,
  ListFilmData,
  ListFilmQuery,
  ListFilmPatch,
} from './services/list-films/list-films.shared';

import { featuredListClient } from './services/featured-list/featured-list.shared';
export type {
  FeaturedList,
  FeaturedListData,
  FeaturedListQuery,
  FeaturedListPatch,
} from './services/featured-list/featured-list.shared';

import { authManagementClient } from './services/auth-management/auth-management.shared';

import { filmEditHistoryClient } from './services/film-edit-history/film-edit-history.shared';
export type {
  FilmEditHistory,
  FilmEditHistoryData,
  FilmEditHistoryQuery,
} from './services/film-edit-history/film-edit-history.shared';

import { filmClient } from './services/film/film.shared';
export type { Film, FilmData, FilmQuery, FilmPatch } from './services/film/film.shared';

import { userClient } from './services/user/user.shared';
export type { User, UserData, UserQuery, UserPatch } from './services/user/user.shared';

import { tspdtClient } from './services/tspdt/tspdt.shared';
export type { Tspdt, TspdtData, TspdtQuery, TspdtPatch } from './services/tspdt/tspdt.shared';

import { directorClient } from './services/director/director.shared';
export type {
  Director,
  DirectorData,
  DirectorQuery,
  DirectorPatch,
} from './services/director/director.shared';

import { castClient } from './services/cast/cast.shared';
export type { Cast, CastData, CastQuery, CastPatch } from './services/cast/cast.shared';

import { tagClient } from './services/tag/tag.shared';
export type { Tag, TagData, TagQuery, TagPatch } from './services/tag/tag.shared';

import { tmdbClient } from './services/tmdb/tmdb.shared';
export type { GetPostersParams } from './services/tmdb/tmdb.shared';

import { imdbClient } from './services/imdb/imdb.shared';
export type { ImdbFilmLookupResult } from './services/imdb/imdb.shared';

import { blurayLookupClient } from './services/bluray-lookup/bluray-lookup.shared';
import { userCollectionClient } from './services/user-collection/user-collection.shared';
export type { BlurayLookupResult } from './services/bluray-lookup/bluray-lookup.shared';

export interface Configuration {
  connection: TransportConnection<ServiceTypes>;
}

export interface ServiceTypes {}

export type ClientApplication = Application<ServiceTypes, Configuration>;

/**
 * Returns a typed client for the best-blurays-backend app.
 *
 * @param connection The REST or Socket.io Feathers client connection
 * @param authenticationOptions Additional settings for the authentication client
 * @see https://dove.feathersjs.com/api/client.html
 * @returns The Feathers client application
 */
export const createClient = (
  connection: TransportConnection<ServiceTypes>,
  authenticationOptions: Partial<AuthenticationClientOptions> = {},
) => {
  const client: ClientApplication = feathers();

  client.configure(connection);
  client.configure(authenticationClient(authenticationOptions));
  client.set('connection', connection);

  client.configure(tspdtClient);
  client.configure(userClient);
  client.configure(filmClient);
  client.configure(filmEditHistoryClient);
  client.configure(authManagementClient);
  client.configure(uhdListsClient);
  client.configure(listFilmsClient);
  client.configure(featuredListClient);
  client.configure(pendingFilmClient);
  client.configure(directorClient);
  client.configure(castClient);
  client.configure(tagClient);
  client.configure(pendingEditClient);
  client.configure(tmdbClient);
  client.configure(imdbClient);
  client.configure(blurayLookupClient);
  client.configure(userCollectionClient);
  return client;
};
